import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, User, PhoneCall, Voicemail, Ban, CalendarPlus, Mail, Mic, Play, Link2, Phone, PhoneForwarded } from 'lucide-react';
import DraggableDialog from './DraggableDialog';
import { TemplateCombobox } from './TemplateCombobox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Reusable Log Call Dialog Component
 * Used by both CRM and Franchise modules
 */
export function LogCallDialog({ 
  open, 
  onClose, 
  lead, 
  callForm, 
  setCallForm, 
  onSubmit, 
  saving,
  templates = [],
  onClickToCall,  // Optional: function to initiate a call
  leadId,         // Optional: lead ID for Telnyx call
}) {
  const [availableRecordings, setAvailableRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [calling, setCalling] = useState(false);
  const [activeCallControlId, setActiveCallControlId] = useState(null);
  
  // VMD (Voicemail Drop) state
  const [vmdTemplates, setVmdTemplates] = useState([]);
  const [selectedVmdId, setSelectedVmdId] = useState(null);
  const [droppingVmd, setDroppingVmd] = useState(false);

  // Fetch VMD templates and user's default on mount
  useEffect(() => {
    const fetchVmdData = async () => {
      try {
        // Fetch all voicemail_drop templates
        const templatesRes = await axios.get(`${API}/templates?type=voicemail_drop`);
        setVmdTemplates(templatesRes.data || []);
        
        // Fetch user's last used VMD template
        const defaultRes = await axios.get(`${API}/telnyx/user-vmd-default`);
        if (defaultRes.data?.default_vmd_template_id) {
          setSelectedVmdId(defaultRes.data.default_vmd_template_id);
        } else if (templatesRes.data?.length > 0) {
          // Default to first template if no preference saved
          setSelectedVmdId(templatesRes.data[0].id);
        }
      } catch {
        // Templates not configured - that's ok
      }
    };
    if (open) {
      fetchVmdData();
    }
  }, [open]);

  // Get all available phone numbers from the lead
  const availablePhoneNumbers = useMemo(() => {
    if (!lead) return [];
    
    const numbers = [];
    const seen = new Set();
    const add = (label, value) => {
      if (value && !seen.has(value)) { seen.add(value); numbers.push({ label, value }); }
    };

    // CRM Lead fields
    add('Mobile', lead.mobile_phone);
    add('Work Direct', lead.work_direct_phone);
    add('Company', lead.work_company_phone);
    add('Work HQ', lead.work_hq_phone);

    // Franchise Lead fields
    add('Phone', lead.phone);
    add('Mobile', lead.mobile);

    // Generic phone field
    add('Phone', lead.phone_number);
    
    return numbers;
  }, [lead]);

  // Handle click-to-call - use WebRTC if available, fall back to server-side API
  const handleInitiateCall = async () => {
    if (!callForm.phone_number) {
      toast.error('Please enter a phone number');
      return;
    }

    // Prefer WebRTC calling (opens TelnyxWebRTCCall dialog)
    if (onClickToCall) {
      onClickToCall(callForm.phone_number);
      return;
    }

    // Fallback: server-side Telnyx API call
    setCalling(true);
    setActiveCallControlId(null);
    try {
      const response = await axios.post(`${API}/telnyx/call`, {
        to_number: callForm.phone_number,
        lead_id: leadId || lead?.id
      });
      
      // Store the call_control_id for VMD functionality
      if (response.data?.call_control_id) {
        setActiveCallControlId(response.data.call_control_id);
      }
      
      toast.success(`Calling ${callForm.phone_number}...`);
    } catch (error) {
      if (error.response?.status === 500 && error.response?.data?.detail?.includes('not configured')) {
        // Telnyx not configured, fall back to tel: link
        window.location.href = `tel:${callForm.phone_number}`;
        toast.info('Opening phone dialer...');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to initiate call');
      }
    } finally {
      setTimeout(() => setCalling(false), 3000);
    }
  };

  // Handle quick VMD drop
  const handleDropVmd = async () => {
    if (!activeCallControlId) {
      toast.error('No active call to drop voicemail on');
      return;
    }
    if (!selectedVmdId) {
      toast.error('Please select a voicemail template');
      return;
    }
    
    setDroppingVmd(true);
    try {
      const response = await axios.post(`${API}/telnyx/quick-vmd`, {
        call_control_id: activeCallControlId,
        template_id: selectedVmdId
      });
      
      toast.success(response.data.message || 'Voicemail dropping - move on to next call!');
      
      // Auto-select "Voicemail" outcome
      setCallForm(f => ({ ...f, call_outcome: 'voicemail' }));
      
      // Clear active call since we're done
      setActiveCallControlId(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to drop voicemail');
    } finally {
      setDroppingVmd(false);
    }
  };


  // Handle ending active call
  const handleEndCall = async () => {
    if (!activeCallControlId) {
      toast.error('No active call to end');
      return;
    }
    
    try {
      await axios.post(`${API}/telnyx/hangup`, {
        call_control_id: activeCallControlId
      });
      
      toast.success('Call ended');
      setActiveCallControlId(null);
      setCalling(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end call');
      // Clear state anyway
      setActiveCallControlId(null);
      setCalling(false);
    }
  };


  // Fetch recent recording for the phone number (from last 15 minutes)
  useEffect(() => {
    const fetchRecentRecording = async () => {
      if (!callForm.phone_number || callForm.phone_number.length < 7) {
        setAvailableRecordings([]);
        return;
      }
      
      setLoadingRecordings(true);
      try {
        // First try to get the most recent recording (smart fetch - last 15 mins)
        const recentRes = await axios.get(`${API}/telnyx/recordings/recent-for-call?phone=${encodeURIComponent(callForm.phone_number)}&minutes=15`);
        
        if (recentRes.data.found && recentRes.data.recording) {
          setAvailableRecordings([recentRes.data.recording]);
          // Auto-select if just downloaded
          if (recentRes.data.just_downloaded) {
            setSelectedRecording(recentRes.data.recording);
            setCallForm(f => ({
              ...f,
              recording_id: recentRes.data.recording.id,
              duration_seconds: recentRes.data.recording.duration_secs || f.duration_seconds
            }));
          }
        } else {
          // Fallback to find-by-phone for older/unlinked recordings
          const fallbackRes = await axios.get(`${API}/telnyx/recordings/find-by-phone?phone=${encodeURIComponent(callForm.phone_number)}&unlinked_only=true`);
          setAvailableRecordings(fallbackRes.data.recordings || []);
        }
      } catch {
        // Fallback if new endpoint fails
        try {
          const res = await axios.get(`${API}/telnyx/recordings/find-by-phone?phone=${encodeURIComponent(callForm.phone_number)}&unlinked_only=true`);
          setAvailableRecordings(res.data.recordings || []);
        } catch {
          setAvailableRecordings([]);
        }
      } finally {
        setLoadingRecordings(false);
      }
    };
    
    if (open) {
      const timer = setTimeout(fetchRecentRecording, 500);
      return () => clearTimeout(timer);
    }
  }, [callForm.phone_number, open]);

  // Handle recording selection
  const handleSelectRecording = (recording) => {
    if (selectedRecording?.id === recording.id) {
      setSelectedRecording(null);
      setCallForm(f => ({ ...f, recording_id: null, duration_seconds: f.duration_seconds }));
    } else {
      setSelectedRecording(recording);
      setCallForm(f => ({ 
        ...f, 
        recording_id: recording.id,
        duration_seconds: recording.duration_secs || f.duration_seconds
      }));
    }
  };

  const formatDuration = (secs) => {
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  const parseDuration = (val) => {
    const match = val.match(/^(\d+):(\d{0,2})$/);
    if (match) {
      return (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
    }
    return /^\d+$/.test(val) ? parseInt(val) || 0 : callForm.duration_seconds;
  };

  const leadName = [lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || 'Unknown';
  const leadCompany = lead?.company_name || lead?.current_employer;

  return (
    <DraggableDialog open={open} onClose={onClose} title="Log Call" maxWidth="max-w-lg">
      <div className="space-y-4">
        {/* Lead Info Header */}
        <div className="bg-slate-50 rounded-lg p-3 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">{leadName}</p>
              {leadCompany && <p className="text-sm text-slate-500">{leadCompany}</p>}
            </div>
          </div>
        </div>

        {/* VMD (Voicemail Drop) Selection - FIRST, before phone */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <PhoneForwarded className="h-4 w-4 text-purple-600" />
            Voicemail Drop
          </Label>
          {vmdTemplates.length > 0 ? (
            <Select
              value={selectedVmdId || ''}
              onValueChange={(value) => setSelectedVmdId(value)}
            >
              <SelectTrigger className="w-full bg-white border-purple-200" data-testid="vmd-select">
                <SelectValue placeholder="Select voicemail message..." />
              </SelectTrigger>
              <SelectContent>
                {vmdTemplates.map((vmd) => (
                  <SelectItem key={vmd.id} value={vmd.id}>
                    <div className="flex items-center gap-2">
                      <span>{vmd.name}</span>
                      {vmd.duration_seconds && (
                        <span className="text-xs text-slate-400">
                          ({Math.round(vmd.duration_seconds)}s)
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-slate-500 italic">
              No voicemail templates. Create one in Templates → Voicemail.
            </p>
          )}
        </div>

        {/* Phone Number with Call & Drop VM Buttons */}
        <div className="space-y-2">
          <Label>Phone Number *</Label>
          <div className="flex gap-2">
            {availablePhoneNumbers.length > 1 ? (
              <Select
                value={callForm.phone_number}
                onValueChange={(value) => setCallForm(f => ({ ...f, phone_number: value }))}
              >
                <SelectTrigger className="flex-1" data-testid="call-phone-select">
                  <SelectValue placeholder="Select phone number" />
                </SelectTrigger>
                <SelectContent>
                  {availablePhoneNumbers.map((phone, idx) => (
                    <SelectItem key={idx} value={phone.value}>
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-xs text-slate-500 w-20">{phone.label}:</span>
                        <span className="font-mono">{phone.value}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input 
                value={callForm.phone_number} 
                onChange={e => setCallForm(f => ({ ...f, phone_number: e.target.value }))} 
                placeholder="+1234567890"
                data-testid="call-phone"
                className="flex-1"
              />
            )}
            <Button 
              type="button"
              variant="default"
              onClick={activeCallControlId ? handleEndCall : handleInitiateCall}
              disabled={calling || !callForm.phone_number}
              className={activeCallControlId ? "bg-red-600 hover:bg-red-700 text-white px-4" : "bg-green-600 hover:bg-green-700 text-white px-4"}
              data-testid="initiate-call-btn"
            >
              {calling ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Calling...</>
              ) : activeCallControlId ? (
                <><PhoneCall className="h-4 w-4 mr-1" /> End</>
              ) : (
                <><Phone className="h-4 w-4 mr-1" /> Call</>
              )}
            </Button>
            {/* Drop VM Button - visible when VMD selected and call active */}
            <Button 
              type="button"
              onClick={handleDropVmd}
              disabled={droppingVmd || !activeCallControlId || !selectedVmdId}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3"
              data-testid="drop-vmd-btn"
              title={!selectedVmdId ? "Select a voicemail first" : !activeCallControlId ? "Start a call first" : "Drop voicemail & move on"}
            >
              {droppingVmd ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PhoneForwarded className="h-4 w-4" />
              )}
            </Button>
          </div>
          {/* Status hint */}
          {selectedVmdId && (
            <p className="text-xs text-purple-600">
              {activeCallControlId 
                ? "📞 Call active — Click purple button to drop voicemail & move on!" 
                : "Click Call, then when you hear voicemail, click purple button to drop message"}
            </p>
          )}
        </div>

        {/* Call Outcome Toggle */}
        <div className="space-y-3">
          <Label>Call Outcome</Label>
          <div className="flex gap-3">
            {[
              { value: 'connected', label: 'Connected', icon: PhoneCall, color: 'green' },
              { value: 'voicemail', label: 'Voicemail (VM)', icon: Voicemail, color: 'amber' },
            ].map(({ value, label, icon: Icon, color }) => (
              <button
                key={value}
                type="button"
                onClick={() => setCallForm(f => ({ ...f, call_outcome: value }))}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  callForm.call_outcome === value 
                    ? `border-${color}-500 bg-${color}-50 text-${color}-700` 
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                }`}
                style={callForm.call_outcome === value ? {
                  borderColor: color === 'green' ? '#22c55e' : '#f59e0b',
                  backgroundColor: color === 'green' ? '#f0fdf4' : '#fffbeb',
                  color: color === 'green' ? '#15803d' : '#b45309'
                } : {}}
                data-testid={`outcome-${value}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Available Recordings */}
        {availableRecordings.length > 0 && (
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-indigo-600" />
              Available Recordings ({availableRecordings.length})
            </Label>
            <div className="max-h-32 overflow-y-auto space-y-2 border rounded-lg p-2 bg-slate-50">
              {availableRecordings.map(rec => (
                <div 
                  key={rec.id}
                  onClick={() => handleSelectRecording(rec)}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                    selectedRecording?.id === rec.id 
                      ? 'bg-indigo-100 border-2 border-indigo-400' 
                      : 'bg-white border border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    selectedRecording?.id === rec.id ? 'bg-indigo-600 text-white' : 'bg-slate-200'
                  }`}>
                    {selectedRecording?.id === rec.id ? <Link2 className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {rec.from_number} → {rec.to_number}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDuration(rec.duration_secs || 0)} • {new Date(rec.recording_started_at).toLocaleString()}
                    </p>
                  </div>
                  <audio 
                    src={`${API}${rec.playback_url}`}
                    className="h-6 w-24"
                    controls
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              ))}
            </div>
            {selectedRecording && (
              <p className="text-xs text-indigo-600 flex items-center gap-1">
                <Link2 className="h-3 w-3" />
                Recording will be linked to this call log
              </p>
            )}
          </div>
        )}
        
        {loadingRecordings && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking for recordings...
          </div>
        )}
        
        {/* Duration */}
        <div className="space-y-2">
          <Label>Duration <span className="text-slate-400 font-normal ml-1">- Auto-filled from call</span></Label>
          <div className="flex items-center gap-2">
            <Input 
              type="text" 
              value={formatDuration(callForm.duration_seconds || 0)} 
              onChange={e => setCallForm(f => ({ ...f, duration_seconds: parseDuration(e.target.value) }))}
              placeholder="0:00"
              className="font-mono text-lg"
              data-testid="call-duration"
            />
            <span className="text-sm text-slate-500">(MM:SS)</span>
          </div>
        </div>
        
        {/* Notes */}
        <div className="space-y-2">
          <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Textarea 
            value={callForm.notes} 
            onChange={e => setCallForm(f => ({ ...f, notes: e.target.value }))} 
            rows={3}
            placeholder="Call notes..."
            className="resize-none"
            data-testid="call-notes"
          />
        </div>
        
        {/* Schedule Follow-up */}
        <OptionCheckbox
          checked={callForm.schedule_followup}
          onChange={v => setCallForm(f => ({ ...f, schedule_followup: v }))}
          icon={CalendarPlus}
          label="Schedule a follow-up call task"
          variant="default"
          testId="schedule-followup"
        />
        
        {callForm.schedule_followup && (
          <div className="ml-6 p-3 bg-white border border-slate-200 rounded-lg">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={callForm.followup_date || ''}
                  onChange={e => setCallForm(f => ({ ...f, followup_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-9"
                  data-testid="followup-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Time</Label>
                <Input
                  type="time"
                  value={callForm.followup_time || '09:00'}
                  onChange={e => setCallForm(f => ({ ...f, followup_time: e.target.value }))}
                  className="h-9"
                  data-testid="followup-time"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Do Not Call */}
        <OptionCheckbox
          checked={callForm.mark_dnc}
          onChange={v => setCallForm(f => ({ ...f, mark_dnc: v }))}
          icon={Ban}
          label="Add to Do Not Call list"
          variant="danger"
          testId="mark-dnc"
        />
        
        {/* Follow-up Email */}
        <div className="pt-4 border-t border-slate-200">
          <OptionCheckbox
            checked={callForm.send_email_after}
            onChange={v => setCallForm(f => ({ ...f, send_email_after: v }))}
            icon={Mail}
            label="Send follow-up email after call"
            variant="primary"
            suffix={<span className="text-xs opacity-70 ml-auto">Optional</span>}
            testId="send-email-after"
          />
          
          {callForm.send_email_after && templates.length > 0 && (
            <div className="mt-3 ml-6 p-3 bg-white rounded-lg border">
              <Label className="text-sm text-slate-600">Email Template</Label>
              <TemplateCombobox
                templates={templates.filter(t => t.type === 'email')}
                value={callForm.email_template_id}
                onChange={id => setCallForm(f => ({ ...f, email_template_id: id }))}
                placeholder="Search templates..."
                testId="quick-email-template"
              />
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="log-call-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <PhoneCall className="h-4 w-4 mr-2" />
          Log Call
        </Button>
      </div>
    </DraggableDialog>
  );
}

// Helper component for checkbox options
function OptionCheckbox({ checked, onChange, icon: Icon, label, variant, suffix, testId }) {
  const variants = {
    default: 'bg-slate-50 border-slate-200 hover:bg-slate-100',
    danger: 'bg-red-50 border-red-200 hover:bg-red-100',
    primary: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
  };
  const iconColors = {
    default: 'text-indigo-600',
    danger: 'text-red-600',
    primary: 'text-indigo-600',
  };
  const labelColors = {
    default: 'text-slate-700',
    danger: 'text-red-700',
    primary: 'text-indigo-700',
  };

  return (
    <div 
      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${variants[variant]}`}
      onClick={() => onChange(!checked)}
    >
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} />
      <Icon className={`h-4 w-4 ${iconColors[variant]}`} />
      <span className={`text-sm font-medium ${labelColors[variant]}`}>{label}</span>
      {suffix}
    </div>
  );
}

export default LogCallDialog;
